import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import { DEFAULT_HOME_ROUTE } from '../../../config/navAccessMap';
import {
  fetchInviteRegistrationDetails,
  parseInviteRegistrationContext,
  pickPreferredOrganizationId,
} from '@saas-foundation/shared-frontend/utils/inviteRegistration';
import { OrganizationSelectFields } from '@saas-foundation/shared-frontend/auth/OrganizationSelectFields';
import PasswordInput from '@saas-foundation/shared-frontend/auth/PasswordInput';
import { getSaasApiBaseUrl } from '../../../utils/saasApiBaseUrl';

import {
  CURRENT_BAA_VERSION,
  CURRENT_PRIVACY_VERSION,
  CURRENT_TERMS_VERSION,
  getLegalBaseUrl,
} from '@saas-foundation/shared-frontend/utils/legalVersions';

const RegisterForm = () => {
  const [searchParams] = useSearchParams();
  const inviteContext = parseInviteRegistrationContext(searchParams);
  const { isInviteRegistration, inviteToken, emailParam, redirectPath } = inviteContext;

  const [invitation, setInvitation] = useState(null);
  const [formData, setFormData] = useState({
    email: emailParam || '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    organization_name: '',
    organization_type: 'billing_company',
    baa_accepted: false,
    terms_accepted: false,
    privacy_accepted: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [emailAlreadyRegistered, setEmailAlreadyRegistered] = useState(false);
  const [postRegisterOrgSelection, setPostRegisterOrgSelection] = useState(null);
  const [selectedOrgId, setSelectedOrgId] = useState('');

  const { register, login, completeLogin } = useAuthContext();
  const navigate = useNavigate();
  const legalBase = getLegalBaseUrl();

  useEffect(() => {
    if (emailParam) {
      setFormData((prev) => ({ ...prev, email: emailParam }));
    }
  }, [emailParam]);

  useEffect(() => {
    if (!isInviteRegistration || !inviteToken) {
      return undefined;
    }

    let cancelled = false;
    (async () => {
      const details = await fetchInviteRegistrationDetails(getSaasApiBaseUrl(), inviteToken);
      if (cancelled || !details) {
        return;
      }
      setInvitation(details);
      if (details.email) {
        setFormData((prev) => ({ ...prev, email: details.email }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isInviteRegistration, inviteToken]);

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
    setError('');
    setSuccess('');
    setEmailAlreadyRegistered(false);
  };

  const validateForm = () => {
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }
    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      setError('Password must contain at least one uppercase letter, one lowercase letter, and one number');
      return false;
    }

    if (isInviteRegistration) {
      return true;
    }

    if (!formData.baa_accepted || !formData.terms_accepted || !formData.privacy_accepted) {
      setError('You must accept the BAA, Terms of Service, and Privacy Policy to start a trial.');
      return false;
    }
    if (!formData.organization_name.trim()) {
      setError('Organization name is required.');
      return false;
    }
    if (formData.organization_name.trim().length < 2) {
      setError('Organization name must be at least 2 characters.');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    try {
      if (postRegisterOrgSelection?.loginToken && selectedOrgId) {
        await completeLogin(postRegisterOrgSelection.loginToken, selectedOrgId);
        navigate(DEFAULT_HOME_ROUTE, { replace: true });
        return;
      }

      if (postRegisterOrgSelection) {
        setError('Please select an organization.');
        setLoading(false);
        return;
      }

      const { confirmPassword, organization_name, organization_type, baa_accepted, terms_accepted, privacy_accepted, ...baseData } = formData;

      const payload = isInviteRegistration
        ? {
            ...baseData,
            source_app: 'agentic_rcm',
          }
        : {
            ...baseData,
            organization_name: organization_name.trim(),
            organization_type,
            baa_accepted,
            terms_accepted,
            privacy_accepted,
            baa_version: CURRENT_BAA_VERSION,
            terms_version: CURRENT_TERMS_VERSION,
            privacy_version: CURRENT_PRIVACY_VERSION,
            source_app: 'agentic_rcm',
          };

      const result = await register(payload);

      if (result.success) {
        const joinedInvite = result.data?.invitation_accepted;
        if (joinedInvite) {
          try {
            const loginResult = await login({ email: formData.email, password: formData.password });
            if (loginResult?.success && loginResult.requiresOrganizationSelection) {
              const preferredOrgId = invitation?.organization?.id ? String(invitation.organization.id) : null;
              setPostRegisterOrgSelection({
                organizations: loginResult.organizations,
                loginToken: loginResult.loginToken,
              });
              setSelectedOrgId(pickPreferredOrganizationId(loginResult.organizations, preferredOrgId));
              setSuccess('Account created. Choose which organization to sign in to.');
              return;
            }
            if (loginResult?.success && !loginResult.requiresMfa && !loginResult.requiresOrganizationSelection) {
              navigate(DEFAULT_HOME_ROUTE, { replace: true });
              return;
            }
          } catch {
            /* fall through to sign-in prompt */
          }
          setSuccess('Account created and invitation accepted. Please sign in to continue.');
          setTimeout(() => navigate('/login', { replace: true }), 1500);
          return;
        }

        setSuccess(result.data?.message || 'Account created. Please verify your email before signing in.');
        setTimeout(
          () => navigate(`/verify?email=${encodeURIComponent(formData.email)}`, { replace: true }),
          1500,
        );
      } else {
        setError(result.error || 'Registration failed');
      }
    } catch (err) {
      const msg = err.message || 'Registration failed. Please try again.';
      setError(msg);
      setEmailAlreadyRegistered(/already registered/i.test(msg));
    } finally {
      setLoading(false);
    }
  };

  const orgName = invitation?.organization?.name;
  const inviteRole = invitation?.role;

  return (
    <div className="flex justify-center items-center min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md p-8 bg-white rounded-lg shadow-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">AgenticPractice</h1>
          <p className="text-gray-600 mt-2">AI-Native all-in-one RCM Platform</p>
          <h2 className="text-2xl font-semibold text-gray-800 mt-6">
            {isInviteRegistration ? 'Create your account' : 'Create Account'}
          </h2>
          <p className="text-gray-600 mt-2">
            {isInviteRegistration && orgName ? (
              <>
                Join <strong>{orgName}</strong>
                {inviteRole ? <> as <strong>{inviteRole}</strong></> : null}
              </>
            ) : (
              'Sign up for a new organization trial'
            )}
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {emailAlreadyRegistered && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
            <p className="text-sm text-amber-800 mb-2">This email is already registered. Do you want to reset your password?</p>
            <Link
              to={`/forgot-password${formData.email ? `?email=${encodeURIComponent(formData.email)}` : ''}`}
              className="text-sm font-medium text-blue-600 hover:text-blue-700"
            >
              Yes, reset my password
            </Link>
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
            <p className="text-sm text-green-600">{success}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
              <input
                id="first_name"
                name="first_name"
                type="text"
                required
                value={formData.first_name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
              <input
                id="last_name"
                name="last_name"
                type="text"
                required
                value={formData.last_name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {!isInviteRegistration && (
            <div>
              <label htmlFor="organization_name" className="block text-sm font-medium text-gray-700 mb-1">Organization Name</label>
              <input
                id="organization_name"
                name="organization_name"
                type="text"
                required
                value={formData.organization_name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Your company or practice name"
              />
              <p className="mt-1 text-xs text-gray-500">Legal or DBA name of the organization you represent</p>
            </div>
          )}

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
            <input
              id="email"
              name="email"
              type="email"
              required
              value={formData.email}
              onChange={handleChange}
              readOnly={isInviteRegistration}
              disabled={isInviteRegistration}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isInviteRegistration ? 'bg-gray-100 text-gray-700' : ''}`}
              placeholder="you@example.com"
            />
            {isInviteRegistration && (
              <p className="mt-1 text-xs text-gray-500">Use the email address that received the invitation.</p>
            )}
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <PasswordInput
              id="password"
              name="password"
              required
              value={formData.password}
              onChange={handleChange}
              autoComplete="new-password"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="••••••••"
            />
            <p className="mt-1 text-xs text-gray-500">Must be at least 8 characters with uppercase, lowercase, and number</p>
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
            <PasswordInput
              id="confirmPassword"
              name="confirmPassword"
              required
              value={formData.confirmPassword}
              onChange={handleChange}
              autoComplete="new-password"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="••••••••"
            />
          </div>

          {postRegisterOrgSelection && (
            <OrganizationSelectFields
              organizations={postRegisterOrgSelection.organizations}
              selectedOrgId={selectedOrgId}
              onChange={(e) => setSelectedOrgId(e.target.value)}
              onBack={() => {
                setPostRegisterOrgSelection(null);
                setSelectedOrgId('');
                setSuccess('');
              }}
              inviteOrgHint={
                invitation?.organization
                  ? {
                      id: String(invitation.organization.id),
                      name: invitation.organization.name,
                      role: invitation.role,
                    }
                  : null
              }
            />
          )}

          {!isInviteRegistration && (
            <>
              <input type="hidden" name="organization_type" value="billing_company" />
              <div className="space-y-3 rounded-md border border-gray-200 bg-gray-50 p-3">
                <p className="text-sm font-medium text-gray-800">Trial agreement (required)</p>
                <label className="flex items-start gap-2 text-sm text-gray-700">
                  <input type="checkbox" name="baa_accepted" checked={formData.baa_accepted} onChange={handleChange} className="mt-1" />
                  <span>
                    I am authorized to bind my organization and accept the{' '}
                    <a href={`${legalBase}/baa`} target="_blank" rel="noreferrer" className="text-blue-600 underline">
                      Business Associate Agreement
                    </a>{' '}
                    (HIPAA) for this trial (version {CURRENT_BAA_VERSION}).
                  </span>
                </label>
                <label className="flex items-start gap-2 text-sm text-gray-700">
                  <input type="checkbox" name="terms_accepted" checked={formData.terms_accepted} onChange={handleChange} className="mt-1" />
                  <span>
                    I agree to the{' '}
                    <a href={`${legalBase}/terms`} target="_blank" rel="noreferrer" className="text-blue-600 underline">Terms of Service</a>.
                  </span>
                </label>
                <label className="flex items-start gap-2 text-sm text-gray-700">
                  <input type="checkbox" name="privacy_accepted" checked={formData.privacy_accepted} onChange={handleChange} className="mt-1" />
                  <span>
                    I agree to the{' '}
                    <a href={`${legalBase}/privacy`} target="_blank" rel="noreferrer" className="text-blue-600 underline">Privacy Policy</a>.
                  </span>
                </label>
              </div>
            </>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Creating account...' : postRegisterOrgSelection ? 'Continue to dashboard' : isInviteRegistration ? 'Create account & join team' : 'Create Account'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Already have an account?{' '}
            <Link
              to={isInviteRegistration && redirectPath ? `/login?redirect=${encodeURIComponent(redirectPath)}` : '/login'}
              className="font-medium text-blue-600 hover:text-blue-500"
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterForm;
